import {DataStatus, Dialog} from 'platform/components';

import {useRef, useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {
  isDefiniteNewVehicleRejection,
  newVehicleApi,
  useCreateFeatureMutation,
  usePutTrimFeatureAssignmentsMutation,
  useListMakeQuery,
  useListModelQuery,
  useListModelYearQuery,
  useListTrimQuery,
  type FeatureResponseBody,
} from '@dms/api/new-vehicle';

import type {ApiError} from 'shared/web';

import {
  catalogueErrorMessage,
  handleCatalogueError,
  t,
  UserFacingCatalogueError,
} from './catalogueUi';
import {emptyEquipmentDraft, equipmentWriteBody, type EquipmentDraft} from './equipmentDraft';
import {NewEquipmentFlow} from './NewEquipmentFlow';
import {NewPricelistVersionDialog} from './NewPricelistVersionDialog';
import type {PricedItemBatchEntry} from './types/PricedItemBatchEntry';
import {ITEM_CHANGE_CANCELLED} from './types/PricedItemChangeOutcome';
import {usePublishedPriceSave} from './usePublishedPriceSave';
import {isInPricelist} from './utils/isInPricelist';
import {trimAssignmentEntry} from './utils/trimAssignmentEntry';
import {trimFeatureChanges} from './utils/trimFeatureChanges';

const attachFailedMessage = (name: string, isFromModelYear: boolean) =>
  isFromModelYear
    ? t(
        'entity.newVehicle.authoring.equipmentAttachFailedInYear',
        '{name} was created, but not on every trim you chose. Save again to retry; no duplicate is created. If you close, finish it on the Equipment by trim tab, where Add from library finds it.',
        {name}
      )
    : t(
        'entity.newVehicle.authoring.equipmentAttachFailed',
        "The equipment item was saved, but some trim attachments weren't. Retry to finish attaching it — no duplicate item will be created."
      );

/**
 * Reuses the guided form for a persisted equipment-library item and complete-set attachments. From a
 * model year the item goes on that year's trims; the year lists only items on its trims or packages.
 */
export function NewCatalogueEquipment(props: {
  modelYearId?: string;
  canViewCost: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isFromModelYear = isNotNil(props.modelYearId);
  const makes = useListMakeQuery({}, {skip: isFromModelYear});
  const models = useListModelQuery({}, {skip: isFromModelYear});
  const years = useListModelYearQuery({}, {skip: isFromModelYear});
  const trims = useListTrimQuery({});
  const [createFeature] = useCreateFeatureMutation();
  const [putAssignments] = usePutTrimFeatureAssignmentsMutation();
  const [getTrim] = newVehicleApi.useLazyGetTrimQuery();
  const [getAssignments] = newVehicleApi.useLazyListTrimFeatureAssignmentsQuery();
  const publishedPrice = usePublishedPriceSave(props.modelYearId);
  const createdId = useRef<string | null>(null);
  // The created item's name; a retry lists the trim prices under it.
  const createdFeature = useRef<FeatureResponseBody | null>(null);
  const [unknownOutcome, setUnknownOutcome] = useState(false);
  const [error, setError] = useState<string>();
  const [submitted, setSubmitted] = useState<EquipmentDraft | null>(null);
  /**
   * Attaches the created item to the chosen trims. Resolves ITEM_CHANGE_CANCELLED when a pricelist
   * version was cancelled: a batch attached none; trim by trim, the trims before it stay attached.
   */
  const attachToTrims = async (intent: EquipmentDraft) => {
    const featureId = createdId.current;
    if (isNil(featureId)) {
      throw new Error('The equipment item is attached only after it was created.');
    }
    // A published year attaches to all chosen trims in one batch with one pricelist version
    // (D107); a draft year has no versions and attaches trim by trim.
    const isOneBatch = await publishedPrice.readIsPublished(intent.scope?.modelYearId);
    const entries: PricedItemBatchEntry[] = [];
    for (const trimId of intent.trimIds) {
      const trim = await getTrim({trimId}, false).unwrap();
      if (trim.modelYearId !== intent.scope?.modelYearId) {
        throw new Error('Selected trim moved outside the submitted model year.');
      }
      const assignments = await getAssignments({trimId}, false).unwrap();
      // The created id is filtered out first, so a trim attached in an earlier pass changes
      // no price on a retry and is written without a second pricelist version.
      const next = [
        ...assignments.filter((assignment) => assignment.featureId !== featureId),
        {
          featureId,
          inclusion: intent.inclusion,
          appliesIf: {anyOfVariantIds: [], allOfFeatureIds: []},
          priceIf: [],
          variantPriceOverrides: [],
          priceNet: intent.inclusion === 'OPTIONAL' ? (intent.trimPrices[trimId] ?? null) : null,
          ...(props.canViewCost ? {costNet: intent.costNet || null} : {}),
          sortOrder: assignments.length,
        },
      ];
      // A Draft trim is outside the price list: it is attached without a price list row.
      const items = !isInPricelist.trim(trim.publicationStatus)
        ? []
        : trimFeatureChanges({
            trimId,
            trimName: trim.name,
            stored: assignments,
            next,
            features: createdFeature.current ? [createdFeature.current] : [],
            variantName: (variantId) => variantId,
          });
      if (isOneBatch) {
        entries.push(
          trimAssignmentEntry({
            trim,
            title: t('entity.newVehicle.catalogue.assignmentOnTrim', '{feature} on {trim}', {
              feature: createdFeature.current?.name ?? intent.name,
              trim: trim.name,
            }),
            items,
            stored: assignments,
            next,
            readTrimVersion: () =>
              getTrim({trimId}, false)
                .unwrap()
                .then((fresh) => fresh.version),
            readAssignments: () => getAssignments({trimId}, false).unwrap(),
          })
        );
        continue;
      }
      // The year is the one chosen in the form, which can differ from the page's.
      const outcome = await publishedPrice.writePricedItemChange({
        modelYearId: intent.scope?.modelYearId,
        items,
        write: (pricelistVersion) =>
          putAssignments({
            trimId,
            replaceTrimFeatureAssignmentsRequestBody: {
              version: trim.version,
              assignments: next,
              ...(pricelistVersion && {pricelistVersion}),
            },
          }).unwrap(),
      });
      if (outcome === ITEM_CHANGE_CANCELLED) {
        return outcome;
      }
    }
    return isOneBatch
      ? publishedPrice.writePricedItemChanges({modelYearId: intent.scope?.modelYearId, entries})
      : 'written';
  };
  if (!trims.data || (!isFromModelYear && (!makes.data || !models.data || !years.data))) {
    return (
      <Dialog
        isOpen
        title={t('entity.newVehicle.authoring.newEquipment', 'New equipment')}
        onClose={props.onClose}
      >
        <DataStatus
          isLoading={makes.isLoading || models.isLoading || years.isLoading || trims.isLoading}
          isError={makes.isError || models.isError || years.isError || trims.isError}
          errorAction={{
            title: t('general.actions.retry', 'Retry'),
            onClick: () => {
              void trims.refetch();
              if (!isFromModelYear) {
                void makes.refetch();
                void models.refetch();
                void years.refetch();
              }
            },
          }}
        />
      </Dialog>
    );
  }
  const carScope =
    makes.data && models.data && years.data
      ? {
          makes: makes.data.items.filter((make) => !make.archived),
          models: models.data.items.filter((model) => !model.archived),
          modelYears: years.data.items.filter(
            (year) => !year.archived && year.status !== 'archived'
          ),
        }
      : undefined;
  const availableTrims = trims.data.items.filter(
    (trim) => !trim.archived && (isNil(props.modelYearId) || trim.modelYearId === props.modelYearId)
  );
  if (props.modelYearId && availableTrims.length === 0) {
    return (
      <Dialog
        isOpen
        title={t('entity.newVehicle.authoring.newEquipment', 'New equipment')}
        onClose={props.onClose}
      >
        <DataStatus
          isEmpty
          emptyMessage={t('entity.newVehicle.authoring.addTrimsFirst', 'Add trim levels first')}
        />
      </Dialog>
    );
  }
  return (
    <>
      <NewEquipmentFlow
        canViewCost={props.canViewCost}
        trims={availableTrims}
        carScope={carScope}
        isTrimRequired={isFromModelYear}
        initialValues={{
          ...emptyEquipmentDraft,
          scope: {
            makeId: '',
            modelId: '',
            modelYearId: props.modelYearId ?? '',
          },
        }}
        scopeLabel={
          props.modelYearId
            ? t(
                'entity.newVehicle.authoring.chooseYearTrims',
                'Choose the trims of this model year that get this equipment.'
              )
            : t(
                'entity.newVehicle.authoring.chooseCarScope',
                'Choose which cars this equipment is for, or skip to keep it in the library only.'
              )
        }
        ownershipMessage={t(
          'entity.newVehicle.authoring.libraryOwnership',
          'This creates an equipment library item. Select trims to make it available on those cars.'
        )}
        errorMessage={error}
        isSaveDisabled={unknownOutcome}
        isIntentLocked={submitted !== null}
        onClose={props.onClose}
        onSave={async (values) => {
          if (unknownOutcome) {
            return;
          }
          setError(undefined);
          const intent = submitted ?? values;
          if (
            intent.trimIds.some(
              (trimId) =>
                !availableTrims.some(
                  (trim) => trim.id === trimId && trim.modelYearId === intent.scope?.modelYearId
                )
            )
          ) {
            setError(
              t(
                'entity.newVehicle.authoring.invalidCarScope',
                'Select trims from the chosen model year before saving.'
              )
            );
            return;
          }
          setSubmitted(intent);
          try {
            if (!createdId.current) {
              // Without an idempotency key, an interrupted POST cannot safely be retried.
              setUnknownOutcome(true);
              const created = await createFeature({
                featureWriteRequestBody: equipmentWriteBody(intent),
              }).unwrap();
              createdId.current = created.id;
              createdFeature.current = created;
              setUnknownOutcome(false);
            }
            if ((await attachToTrims(intent)) === ITEM_CHANGE_CANCELLED) {
              // The item stays created; the trims not attached are attached on Retry.
              setError(attachFailedMessage(intent.name, isFromModelYear));
              return;
            }
            props.onSaved();
          } catch (caught) {
            if (!(caught instanceof Error)) {
              handleCatalogueError(caught as ApiError);
            }
            // A batch too large to send: nothing was sent, the message says what to do.
            if (caught instanceof UserFacingCatalogueError) {
              setError(caught.message);
              return;
            }
            if (!createdId.current && isDefiniteNewVehicleRejection(caught)) {
              setUnknownOutcome(false);
              setSubmitted(null);
              setError(catalogueErrorMessage(caught));
              return;
            }
            setError(
              createdId.current
                ? attachFailedMessage(intent.name, isFromModelYear)
                : t(
                    'entity.newVehicle.authoring.equipmentOutcomeUnknown',
                    "We couldn't confirm whether the item was saved. Check the equipment library before trying again."
                  )
            );
          }
        }}
      />
      <NewPricelistVersionDialog {...publishedPrice.dialogProps} />
    </>
  );
}
